// =============================================================================
// Delegation of Approvals — Mock Data
// PRD §7.2 — all 9 scenarios covered
// =============================================================================

import type {
  ApprovalDelegation,
  DelegationSettings,
  DelegationAuditEntry,
  AdminDelegationListItem,
} from '../types/delegation.types';
import { DEFAULT_DELEGATION_SETTINGS } from '../types/delegation.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function daysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function daysAgo(days: number): string {
  return daysFromNow(-days);
}

function isoNow(): string {
  return new Date().toISOString();
}

// ---------------------------------------------------------------------------
// Mock Users (consistent across scenarios)
// ---------------------------------------------------------------------------

export const MOCK_CURRENT_USER = {
  id: 'user-james-chen',
  name: 'James Chen',
  email: 'james.chen@example.com',
};

export const MOCK_USERS = {
  jamesChen: { id: 'user-james-chen', name: 'James Chen', email: 'james.chen@example.com' },
  priyaSharma: { id: 'user-priya-sharma', name: 'Priya Sharma', email: 'priya.sharma@example.com' },
  marcusWebb: { id: 'user-marcus-webb', name: 'Marcus Webb', email: 'marcus.webb@example.com' },
  oliviaGrant: { id: 'user-olivia-grant', name: 'Olivia Grant', email: 'olivia.grant@example.com' },
  sarahMitchell: { id: 'user-sarah-mitchell', name: 'Sarah Mitchell', email: 'sarah.mitchell@example.com' },
  tomKipling: { id: 'user-tom-kipling', name: 'Tom Kipling', email: 'tom.kipling@example.com' },
};

// ---------------------------------------------------------------------------
// Scenario 1 — Active delegation (James Chen → Priya Sharma, future end date)
// Used for: My Delegations list, active status, approving-on-behalf banner
// ---------------------------------------------------------------------------

export const MOCK_ACTIVE_DELEGATION: ApprovalDelegation = {
  id: 'del-001',
  managerId: MOCK_USERS.jamesChen.id,
  managerName: MOCK_USERS.jamesChen.name,
  managerEmail: MOCK_USERS.jamesChen.email,
  delegateId: MOCK_USERS.priyaSharma.id,
  delegateName: MOCK_USERS.priyaSharma.name,
  delegateEmail: MOCK_USERS.priyaSharma.email,
  startDate: daysAgo(2),
  endDate: daysFromNow(7),
  status: 'active',
  reason: 'annual_leave',
  scope: 'all',
  notes: 'Covering while I am on annual leave. Please approve any submitted timesheets.',
  createdAt: daysAgo(5) + 'T09:00:00.000Z',
  createdBy: MOCK_USERS.jamesChen.id,
  durationDays: 9,

  auditEvents: [
    {
      id: 'audit-001-1',
      delegationId: 'del-001',
      event: 'created',
      actorId: MOCK_USERS.jamesChen.id,
      actorName: MOCK_USERS.jamesChen.name,
      occurredAt: daysAgo(5) + 'T09:00:00.000Z',
    },
    {
      id: 'audit-001-2',
      delegationId: 'del-001',
      event: 'updated',
      actorId: MOCK_USERS.priyaSharma.id,
      actorName: MOCK_USERS.priyaSharma.name,
      notes: 'Delegation acknowledged by delegate.',
      occurredAt: daysAgo(5) + 'T10:30:00.000Z',
    },
  ],
};

// ---------------------------------------------------------------------------
// Scenario 2 — Upcoming active delegation (James Chen → Tom Kipling)
// ---------------------------------------------------------------------------

export const MOCK_PENDING_DELEGATION: ApprovalDelegation = {
  id: 'del-002',
  managerId: MOCK_USERS.jamesChen.id,
  managerName: MOCK_USERS.jamesChen.name,
  managerEmail: MOCK_USERS.jamesChen.email,
  delegateId: MOCK_USERS.tomKipling.id,
  delegateName: MOCK_USERS.tomKipling.name,
  delegateEmail: MOCK_USERS.tomKipling.email,
  startDate: daysFromNow(3),
  endDate: daysFromNow(14),
  status: 'active',
  reason: 'conference',
  scope: 'all',
  notes: 'Attending a conference. Please cover approvals during this period.',
  createdAt: daysAgo(1) + 'T14:00:00.000Z',
  createdBy: MOCK_USERS.jamesChen.id,
  durationDays: 11,
  auditEvents: [
    {
      id: 'audit-002-1',
      delegationId: 'del-002',
      event: 'created',
      actorId: MOCK_USERS.jamesChen.id,
      actorName: MOCK_USERS.jamesChen.name,
      occurredAt: daysAgo(1) + 'T14:00:00.000Z',
    },
  ],
};

// ---------------------------------------------------------------------------
// Scenario 3 — Expired delegation (past, ended naturally)
// ---------------------------------------------------------------------------

export const MOCK_EXPIRED_DELEGATION: ApprovalDelegation = {
  id: 'del-003',
  managerId: MOCK_USERS.jamesChen.id,
  managerName: MOCK_USERS.jamesChen.name,
  managerEmail: MOCK_USERS.jamesChen.email,
  delegateId: MOCK_USERS.oliviaGrant.id,
  delegateName: MOCK_USERS.oliviaGrant.name,
  delegateEmail: MOCK_USERS.oliviaGrant.email,
  startDate: daysAgo(30),
  endDate: daysAgo(23),
  status: 'expired',
  reason: 'sick_leave',
  scope: 'all',
  notes: 'Covering during sick leave.',
  createdAt: daysAgo(32) + 'T08:00:00.000Z',
  createdBy: MOCK_USERS.jamesChen.id,
  durationDays: 7,

  auditEvents: [
    {
      id: 'audit-003-1',
      delegationId: 'del-003',
      event: 'created',
      actorId: MOCK_USERS.jamesChen.id,
      actorName: MOCK_USERS.jamesChen.name,
      occurredAt: daysAgo(32) + 'T08:00:00.000Z',
    },
    {
      id: 'audit-003-2',
      delegationId: 'del-003',
      event: 'expired',
      actorId: 'system',
      actorName: 'System',
      occurredAt: daysAgo(23) + 'T23:59:00.000Z',
    },
  ],
};

// ---------------------------------------------------------------------------
// Scenario 4 — Revoked delegation (manager cancelled early)
// ---------------------------------------------------------------------------

export const MOCK_REVOKED_DELEGATION: ApprovalDelegation = {
  id: 'del-004',
  managerId: MOCK_USERS.jamesChen.id,
  managerName: MOCK_USERS.jamesChen.name,
  managerEmail: MOCK_USERS.jamesChen.email,
  delegateId: MOCK_USERS.priyaSharma.id,
  delegateName: MOCK_USERS.priyaSharma.name,
  delegateEmail: MOCK_USERS.priyaSharma.email,
  startDate: daysAgo(60),
  endDate: daysAgo(50),
  status: 'revoked',
  reason: 'business_travel',
  scope: 'all',
  notes: '',
  createdAt: daysAgo(62) + 'T11:00:00.000Z',
  createdBy: MOCK_USERS.jamesChen.id,
  revokedAt: daysAgo(57) + 'T09:00:00.000Z',
  revokedBy: MOCK_USERS.jamesChen.id,
  revokeReason: 'Returned from travel early.',
  durationDays: 10,

  auditEvents: [
    {
      id: 'audit-004-1',
      delegationId: 'del-004',
      event: 'created',
      actorId: MOCK_USERS.jamesChen.id,
      actorName: MOCK_USERS.jamesChen.name,
      occurredAt: daysAgo(62) + 'T11:00:00.000Z',
    },
    {
      id: 'audit-004-2',
      delegationId: 'del-004',
      event: 'revoked',
      actorId: MOCK_USERS.jamesChen.id,
      actorName: MOCK_USERS.jamesChen.name,
      notes: 'Returned from travel early.',
      occurredAt: daysAgo(57) + 'T09:00:00.000Z',
    },
  ],
};

// ---------------------------------------------------------------------------
// Scenario 5 — Incoming active delegation (Sarah Jones → James Smith as delegate)
// Used for: ApprovingOnBehalfBanner, segmented approvals queue
//
// ⚠️ IMPORTANT: managerId MUST match the numeric employee ID of the delegating
// manager in the mock timesheet data so that ApprovalDashboard can correlate
// timesheets to delegated sections via ts.approverId === delegation.managerId.
//
// Sarah Jones is employee ID 2 in mockData.ts and is the approver for employees
// Hannah Walker (12) and Matthew Hughes (17) — their timesheets have approverId: 2.
// ---------------------------------------------------------------------------

export const MOCK_INCOMING_ACTIVE_DELEGATION: ApprovalDelegation = {
  id: 'del-005',
  // Use numeric string '2' → matches Sarah Jones (id: 2) in mockData.ts
  managerId: '2',
  managerName: 'Sarah Jones',
  managerEmail: 'sarah.jones@timetrackpro.com',
  delegateId: MOCK_USERS.jamesChen.id,
  delegateName: MOCK_USERS.jamesChen.name,
  delegateEmail: MOCK_USERS.jamesChen.email,
  startDate: daysAgo(1),
  endDate: daysFromNow(5),
  status: 'active',
  reason: 'annual_leave',
  scope: 'all',
  notes: 'Please approve any timesheets submitted by my team while I\'m on leave.',
  createdAt: daysAgo(3) + 'T10:00:00.000Z',
  createdBy: '2',
  durationDays: 6,

};

// ---------------------------------------------------------------------------
// Scenario 6 — Incoming active delegation (Olivia Grant → James Chen)
// ---------------------------------------------------------------------------

export const MOCK_INCOMING_PENDING_DELEGATION: ApprovalDelegation = {
  id: 'del-006',
  managerId: MOCK_USERS.oliviaGrant.id,
  managerName: MOCK_USERS.oliviaGrant.name,
  managerEmail: MOCK_USERS.oliviaGrant.email,
  delegateId: MOCK_USERS.jamesChen.id,
  delegateName: MOCK_USERS.jamesChen.name,
  delegateEmail: MOCK_USERS.jamesChen.email,
  startDate: daysFromNow(2),
  endDate: daysFromNow(9),
  status: 'active',
  reason: 'parental_leave',
  scope: 'all',
  notes: 'Starting parental leave soon. Would really appreciate your help.',
  createdAt: daysAgo(0) + 'T08:30:00.000Z',
  createdBy: MOCK_USERS.oliviaGrant.id,
  durationDays: 7,
};



// ---------------------------------------------------------------------------
// Collections for each query scenario
// ---------------------------------------------------------------------------

/** Scenario: Empty state — manager has no delegations at all */
export const MOCK_EMPTY_DELEGATIONS: ApprovalDelegation[] = [];

/** All delegations created BY James Chen (my-delegations) */
export const MOCK_MY_DELEGATIONS: ApprovalDelegation[] = [
  MOCK_ACTIVE_DELEGATION,
  MOCK_PENDING_DELEGATION,
  MOCK_EXPIRED_DELEGATION,
  MOCK_REVOKED_DELEGATION,
];

/** Delegations assigned TO James Chen (delegated-to-me) */
export const MOCK_DELEGATED_TO_ME: ApprovalDelegation[] = [
  MOCK_INCOMING_ACTIVE_DELEGATION,
  MOCK_INCOMING_PENDING_DELEGATION,
];

// ---------------------------------------------------------------------------
// Extra admin-only records — different managers so audit search has variety
// ---------------------------------------------------------------------------

const MOCK_ADMIN_EXTRA_1: ApprovalDelegation = {
  id: 'del-admin-01',
  managerId: MOCK_USERS.marcusWebb.id,
  managerName: MOCK_USERS.marcusWebb.name,
  managerEmail: MOCK_USERS.marcusWebb.email,
  delegateId: MOCK_USERS.sarahMitchell.id,
  delegateName: MOCK_USERS.sarahMitchell.name,
  delegateEmail: MOCK_USERS.sarahMitchell.email,
  startDate: daysAgo(30),
  endDate: daysAgo(23),
  status: 'expired',
  scope: 'all',
  notes: 'Annual leave cover.',
  createdAt: daysAgo(32) + 'T09:00:00.000Z',
  createdBy: MOCK_USERS.marcusWebb.id,
  durationDays: 7,
};

const MOCK_ADMIN_EXTRA_2: ApprovalDelegation = {
  id: 'del-admin-02',
  managerId: MOCK_USERS.sarahMitchell.id,
  managerName: MOCK_USERS.sarahMitchell.name,
  managerEmail: MOCK_USERS.sarahMitchell.email,
  delegateId: MOCK_USERS.tomKipling.id,
  delegateName: MOCK_USERS.tomKipling.name,
  delegateEmail: MOCK_USERS.tomKipling.email,
  startDate: daysAgo(10),
  endDate: daysFromNow(4),
  status: 'active',
  scope: 'all',
  notes: 'Covering while I attend the leadership offsite.',
  createdAt: daysAgo(12) + 'T08:15:00.000Z',
  createdBy: MOCK_USERS.sarahMitchell.id,
  durationDays: 14,
};

const MOCK_ADMIN_EXTRA_3: ApprovalDelegation = {
  id: 'del-admin-03',
  managerId: MOCK_USERS.tomKipling.id,
  managerName: MOCK_USERS.tomKipling.name,
  managerEmail: MOCK_USERS.tomKipling.email,
  delegateId: MOCK_USERS.priyaSharma.id,
  delegateName: MOCK_USERS.priyaSharma.name,
  delegateEmail: MOCK_USERS.priyaSharma.email,
  startDate: daysAgo(60),
  endDate: daysAgo(53),
  status: 'revoked',
  scope: 'all',
  notes: 'Sick leave.',
  revokeReason: 'Returned to work earlier than expected.',
  createdAt: daysAgo(62) + 'T11:00:00.000Z',
  createdBy: MOCK_USERS.tomKipling.id,
  revokedAt: daysAgo(56) + 'T14:00:00.000Z',
  revokedBy: MOCK_USERS.tomKipling.id,
  durationDays: 7,
};

const MOCK_ADMIN_EXTRA_4: ApprovalDelegation = {
  id: 'del-admin-04',
  managerId: MOCK_USERS.oliviaGrant.id,
  managerName: MOCK_USERS.oliviaGrant.name,
  managerEmail: MOCK_USERS.oliviaGrant.email,
  delegateId: MOCK_USERS.marcusWebb.id,
  delegateName: MOCK_USERS.marcusWebb.name,
  delegateEmail: MOCK_USERS.marcusWebb.email,
  startDate: daysAgo(5),
  endDate: daysFromNow(9),
  status: 'active',
  scope: 'all',
  notes: 'Maternity leave handover.',
  createdAt: daysAgo(7) + 'T10:30:00.000Z',
  createdBy: MOCK_USERS.oliviaGrant.id,
  durationDays: 14,
};

/** Admin audit view — all delegations across the org */
export const MOCK_ALL_DELEGATIONS_ADMIN: ApprovalDelegation[] = [
  MOCK_ACTIVE_DELEGATION,
  MOCK_PENDING_DELEGATION,
  MOCK_EXPIRED_DELEGATION,
  MOCK_REVOKED_DELEGATION,
  MOCK_INCOMING_ACTIVE_DELEGATION,
  MOCK_INCOMING_PENDING_DELEGATION,
  MOCK_ADMIN_EXTRA_1,
  MOCK_ADMIN_EXTRA_2,
  MOCK_ADMIN_EXTRA_3,
  MOCK_ADMIN_EXTRA_4,
];

// ---------------------------------------------------------------------------
// Mock Settings
// ---------------------------------------------------------------------------

export const MOCK_DELEGATION_SETTINGS: DelegationSettings = {
  ...DEFAULT_DELEGATION_SETTINGS,
  maxDelegationDays: 90,
};

// ---------------------------------------------------------------------------
// Mock pending timesheet count (PRD §4 Feature B — reassign at creation time)
// Controls whether the reassign prompt is shown on the confirmation screen.
// Set to 0 to test the hidden-prompt path.
// ---------------------------------------------------------------------------

/**
 * Number of pending timesheets currently in the manager's approvals queue.
 * Used by `getPendingTimesheetCount()` in mock mode.
 */
export const MOCK_PENDING_TIMESHEET_COUNT = 8;

// ---------------------------------------------------------------------------
// Error trigger IDs for mock conflict simulation (PRD §7.2)
// Pass these as delegateId in createDelegation to trigger specific errors
// ---------------------------------------------------------------------------

export const MOCK_ERROR_TRIGGERS = {
  /** delegateId that triggers overlap_conflict (409) */
  OVERLAP_CONFLICT: 'trigger-overlap',
  /** delegateId that triggers circular_delegation (400) */
  CIRCULAR_DELEGATION: 'trigger-circular',
  /** delegateId that triggers delegate_not_eligible (400) */
  NOT_ELIGIBLE: 'trigger-not-eligible',
  /** delegateId that triggers self_delegation (400) */
  SELF_DELEGATION: MOCK_CURRENT_USER.id,
};

// ---------------------------------------------------------------------------
// Mock admin audit report items
// ---------------------------------------------------------------------------

export const MOCK_ADMIN_AUDIT_ITEMS: AdminDelegationListItem[] = MOCK_ALL_DELEGATIONS_ADMIN.map(
  (delegation) => ({
    delegation,
    events: delegation.auditEvents ?? [],
  })
);
